import React, { useState } from 'react';

import { ScreenHead } from '../../ui/screen-parts.js';
import { Chip, Field, Panel, Empty, useFlash, readForm } from '../../ui/kit.js';
import { useBuilding, RESIDENT } from '../../state/building.js';

import Renovation from '../../media/images/modules/form-renovation.jpg';
import Contractor from '../../media/images/modules/form-contractor.jpg';
import Key from '../../media/images/modules/form-key.jpg';
import Pet from '../../media/images/modules/form-pet.jpg';
import Guest from '../../media/images/modules/form-guest.jpg';

const STATUS_TONE = { 'Pending': 'warn', 'Approved': 'ok', 'Declined': 'alert' };

/* Each form's fields. The first one titles the submission. */
const CATALOG = {
    'Renovation request': {
        blurb: 'Board approval for work beyond paint and fixtures.',
        image: Renovation,
        fields: [
            { name: 'title', label: 'Project', placeholder: 'e.g. Primary bath remodel' },
            { name: 'contractor', label: 'Licensed contractor', placeholder: 'Company and license number' },
            { name: 'duration', label: 'Estimated duration', placeholder: 'e.g. 4 weeks' },
            { name: 'scope', label: 'Scope of work', textarea: true, placeholder: 'Rooms, trades, any plumbing or electrical changes' }
        ]
    },
    'Contractor access': {
        blurb: 'Lets a vendor through security for a set window.',
        image: Contractor,
        fields: [
            { name: 'title', label: 'Company', placeholder: 'e.g. Brickell Tile & Stone' },
            { name: 'crew', label: 'Crew names', placeholder: 'Who will be on site' },
            { name: 'dates', label: 'Dates', placeholder: 'e.g. Oct 6 to Oct 10, 8 AM to 5 PM' },
            { name: 'coi', label: 'Insurance certificate', placeholder: 'On file / attached' }
        ]
    },
    'Key/fob replacement': {
        blurb: 'Replace a lost fob. The old one is deactivated right away.',
        image: Key,
        fields: [
            { name: 'title', label: 'Item', placeholder: 'e.g. Lobby fob, garage remote' },
            { name: 'count', label: 'Quantity', placeholder: '1' },
            { name: 'reason', label: 'Reason', textarea: true, placeholder: 'Lost, damaged, additional household member' }
        ]
    },
    'Pet addendum': {
        blurb: 'Adds a pet to your unit agreement.',
        image: Pet,
        fields: [
            { name: 'title', label: 'Pet name', placeholder: 'e.g. Mango' },
            { name: 'breed', label: 'Type and breed', placeholder: 'e.g. Dog, golden retriever' },
            { name: 'weight', label: 'Weight (lb)', placeholder: '62' },
            { name: 'vet', label: 'Veterinarian', placeholder: 'Clinic and phone' }
        ]
    },
    'Short-term guest authorization': {
        blurb: 'Guests staying while you are away need board approval.',
        image: Guest,
        fields: [
            { name: 'title', label: 'Guest name', placeholder: 'e.g. Sofia Rivera' },
            { name: 'dates', label: 'Stay dates', placeholder: 'e.g. Dec 20 to Jan 3' },
            { name: 'party', label: 'Party size', placeholder: '2 adults, 1 child' },
            { name: 'contact', label: 'Guest phone', placeholder: '(305) 555-0142' }
        ]
    }
};

/* Digital Forms / Approvals: what the resident submits lands on the
   staff Approvals screen, and its decision shows back here. */
function FormsScreen() {
    const { approvals, add } = useBuilding();
    const [flash, notify] = useFlash();
    const [kind, setKind] = useState('Renovation request');
    const from = `Unit ${RESIDENT.unit}`;
    const mine = approvals.filter(a => a.from === from);
    const form = CATALOG[kind];

    const submit = event => {
        /* The agreement box was never read, so unticking it changed
           nothing. It is checked before readForm, which would otherwise
           clear everything the resident typed. */
        if (!event.currentTarget.elements.agree.checked) {
            event.preventDefault();
            return notify('Tick the box to agree to the association rules, then submit again.');
        }

        const values = readForm(event, ['title']);
        if (!values) return notify(`Fill in ${form.fields[0].label.toLowerCase()} first.`);

        const detail = form.fields.slice(1)
            .filter(f => String(values[f.name] || '').trim())
            .map(f => `${f.label}: ${values[f.name]}`)
            .join('. ');

        add('approvals', { title: values.title, from, kind, detail: detail || 'No further details.', status: 'Pending' });
        notify(`${kind} submitted. Management will review it and you will see the decision here.`);
    };

    return (
        <div className="screen-content">
            <ScreenHead eyebrow="Digital forms" title="Forms and approvals" />
            {flash}

            <div className="tiles">
                {Object.keys(CATALOG).map(name => (
                    <button
                        type="button"
                        className="tile tile--photo"
                        key={name}
                        aria-pressed={kind === name}
                        onClick={() => setKind(name)}
                    >
                        <img src={CATALOG[name].image} alt="" loading="lazy" />
                        <span>{name}</span>
                    </button>
                ))}
            </div>

            <div className="split section-gap">
                <Panel as="form" className="panel--accent" onSubmit={submit} key={kind}>
                    <h4>{kind}</h4>
                    <p className="row__meta">{form.blurb}</p>
                    <div className="form">
                        {form.fields.map(f => (
                            <Field label={f.label} key={f.name} wide={f.textarea}>
                                {f.textarea
                                    ? <textarea name={f.name} className="form-control" rows="3" placeholder={f.placeholder} />
                                    : <input name={f.name} className="form-control" placeholder={f.placeholder} />}
                            </Field>
                        ))}
                        <label className="field field--wide check">
                            <input type="checkbox" name="agree" defaultChecked style={{ width: 'auto' }} />
                            <span>I agree to the association rules for this request</span>
                        </label>
                        <button type="submit" className="btn btn-primary">Submit for approval</button>
                    </div>
                </Panel>

                <div className="stack">
                    <h4>Your submitted forms</h4>
                    {mine.length === 0 ? <Empty>Nothing submitted yet.</Empty> : (
                        <ul className="rows">
                            {mine.map(a => (
                                <li className="row" key={a.id}>
                                    <div className="row__main">
                                        <span className="row__title">{a.title}</span>
                                        <span className="row__meta">{a.kind} · {a.detail}</span>
                                    </div>
                                    <div className="row__end">
                                        <Chip tone={STATUS_TONE[a.status] || 'muted'}>{a.status}</Chip>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}

export default FormsScreen;
